import React, { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';

import { parseColorWithAlpha, rgba } from './colors';
import { linearGradient } from './objects';

/**
 * Small controls shared by the rail panels, the context bar and the inspector.
 * One shape for each kind of control, so the three surfaces read as one tool.
 */

export const SectionLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
    <div className={cn('text-[11px] font-medium tracking-[0.06em] text-muted-foreground uppercase', className)}>{children}</div>
);

/** A collapsible inspector group. */
export const PanelSection: React.FC<{
    title: string;
    defaultOpen?: boolean;
    action?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, defaultOpen = true, action, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border-b border-border/70 px-4 py-3">
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="flex flex-1 cursor-pointer items-center justify-between gap-2 text-xs font-medium text-foreground"
                >
                    <span>{title}</span>
                    <ChevronDown className={cn('size-3.5 text-muted-foreground transition-transform', !open && '-rotate-90')} />
                </button>
                {action}
            </div>
            {open && <div className="mt-3 flex flex-col gap-3">{children}</div>}
        </div>
    );
};

export const SliderField: React.FC<{
    label: string;
    min: number;
    max: number;
    step: number;
    value: number;
    onChange: (v: number) => void;
    suffix?: string;
    precision?: number;
    displayValue?: number | string;
}> = ({ label, min, max, step, value, onChange, suffix = '', precision = 0, displayValue }) => {
    const readout = displayValue !== undefined
        ? (typeof displayValue === 'number' ? displayValue.toFixed(precision) : displayValue)
        : (Number.isFinite(value) ? value : 0).toFixed(precision);
    return (
        <div>
            <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{label}</span>
                <span className="text-xs tabular-nums text-foreground">{readout}{suffix}</span>
            </div>
            <Slider min={min} max={max} step={step} value={[Number.isFinite(value) ? value : 0]} onValueChange={(v) => onChange(v[0])} />
        </div>
    );
};

/** Labelled number input (X, Y, W, H, °). Commits on blur / Enter; arrows step. */
export const NumberField: React.FC<{
    label: string;
    value: number;
    onCommit: (v: number) => void;
    step?: number;
    min?: number;
    max?: number;
    title?: string;
    className?: string;
}> = ({ label, value, onCommit, step = 1, min, max, title, className }) => {
    const [draft, setDraft] = useState(String(Math.round(value)));
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (document.activeElement !== ref.current) setDraft(String(Math.round(value * 10) / 10));
    }, [value]);
    const clamp = (n: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
    const commit = () => {
        const n = Number(draft);
        if (Number.isFinite(n)) onCommit(clamp(n));
        else setDraft(String(Math.round(value)));
    };
    return (
        <label
            title={title}
            className={cn(
                'flex h-8 min-w-0 items-center gap-1.5 rounded-lg border border-transparent bg-foreground/[0.045] px-2 text-xs transition-colors focus-within:border-foreground/30 focus-within:bg-background hover:bg-foreground/[0.07]',
                className,
            )}
        >
            <span className="shrink-0 text-muted-foreground">{label}</span>
            <input
                ref={ref}
                value={draft}
                inputMode="decimal"
                onChange={(e) => setDraft(e.target.value.replace(/[^\d.-]/g, ''))}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { commit(); (e.target as HTMLInputElement).blur(); }
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        const n = clamp((Number(draft) || 0) + (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1));
                        setDraft(String(n));
                        onCommit(n);
                    }
                }}
                className="w-full min-w-0 border-none bg-transparent p-0 tabular-nums text-foreground outline-none"
            />
        </label>
    );
};

/** On/off chip for mutually exclusive or toggled options. */
export const ToggleChip: React.FC<{
    active?: boolean;
    onClick: () => void;
    title?: string;
    className?: string;
    disabled?: boolean;
    children: React.ReactNode;
}> = ({ active, onClick, title, className, disabled, children }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={title}
        aria-pressed={!!active}
        disabled={disabled}
        className={cn(
            'flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-lg border px-2 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:size-4',
            active
                ? 'border-foreground bg-background font-medium text-foreground ring-[0.5px] ring-foreground'
                : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
            className,
        )}
    >
        {children}
    </button>
);

/** A tile in a rail panel grid (shapes, frames, icons, scrims). */
export const Tile: React.FC<{
    label: string;
    onClick: () => void;
    children: React.ReactNode;
    showLabel?: boolean;
    className?: string;
    active?: boolean;
}> = ({ label, onClick, children, showLabel, className, active }) => (
    <button
        type="button"
        onClick={onClick}
        title={label}
        aria-label={label}
        className={cn(
            'group flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border bg-background p-2 text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground',
            active && 'border-foreground ring-[0.5px] ring-foreground',
            className,
        )}
    >
        {children}
        {showLabel && <span className="line-clamp-1 text-[11px] leading-tight">{label}</span>}
    </button>
);

/** Ghost icon button used in the bars. */
export const BarButton = React.forwardRef<HTMLButtonElement, {
    title: string;
    onClick?: () => void;
    active?: boolean;
    destructive?: boolean;
    disabled?: boolean;
    className?: string;
    children: React.ReactNode;
}>(({ title, onClick, active, destructive, disabled, className, children, ...rest }, ref) => (
    <button
        ref={ref}
        type="button"
        onClick={onClick}
        title={title}
        aria-label={title}
        aria-pressed={active}
        disabled={disabled}
        {...rest}
        className={cn(
            'flex h-8 min-w-8 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-2 text-xs whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:size-4',
            active ? 'bg-foreground/[0.08] font-medium text-foreground' : 'text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground',
            destructive && 'text-destructive hover:bg-destructive/10 hover:text-destructive',
            className,
        )}
    >
        {children}
    </button>
));
BarButton.displayName = 'BarButton';

export const BarDivider: React.FC = () => <div className="mx-1 h-5 w-px shrink-0 bg-border" />;

// ─── Colour ─────────────────────────────────────────────────────────────

export type Paint = string | fabric.Gradient;

const isGradient = (p: any): p is fabric.Gradient => !!p && typeof p === 'object' && Array.isArray((p as any).colorStops);

/** CSS preview of a fill (solid or gradient). */
export function paintToCss(p: Paint | undefined | null): string {
    if (!p) return 'transparent';
    if (typeof p === 'string') return p;
    const g: any = p;
    const stops = [...(g.colorStops || [])].sort((a: any, b: any) => a.offset - b.offset)
        .map((s: any) => `${s.color} ${Math.round(s.offset * 100)}%`).join(', ');
    if (g.type === 'radial') return `radial-gradient(circle, ${stops})`;
    const c = g.coords || {};
    const angle = Math.round((Math.atan2((c.y2 ?? 1) - (c.y1 ?? 0), (c.x2 ?? 0) - (c.x1 ?? 0)) * 180) / Math.PI) + 90;
    return `linear-gradient(${angle}deg, ${stops})`;
}

type GradDir = 'down' | 'right' | 'diagonal';

function gradientParts(p: Paint): { a: string; b: string; dir: GradDir } {
    if (!isGradient(p)) {
        const base = typeof p === 'string' ? p : '#1A1917';
        return { a: base, b: '#FFFFFF', dir: 'down' };
    }
    const g: any = p;
    const stops = [...g.colorStops].sort((x: any, y: any) => x.offset - y.offset);
    const c = g.coords || {};
    const dir: GradDir = (c.x2 ?? 0) > (c.x1 ?? 0) && (c.y2 ?? 0) > (c.y1 ?? 0) ? 'diagonal' : (c.x2 ?? 0) !== (c.x1 ?? 0) ? 'right' : 'down';
    return { a: stops[0]?.color || '#000000', b: stops[stops.length - 1]?.color || '#FFFFFF', dir };
}

function buildGradient(a: string, b: string, dir: GradDir): fabric.Gradient {
    if (dir === 'diagonal') {
        return new fabric.Gradient({
            type: 'linear',
            gradientUnits: 'percentage',
            coords: { x1: 0, y1: 0, x2: 1, y2: 1 },
            colorStops: [{ offset: 0, color: a }, { offset: 1, color: b }],
        } as any);
    }
    return linearGradient([{ offset: 0, color: a }, { offset: 1, color: b }], dir === 'right' ? 'right' : 'down');
}

/** Hex + alpha editor with swatches. */
export const SolidPicker: React.FC<{
    value: string;
    onChange: (c: string) => void;
    swatches: string[];
    allowAlpha?: boolean;
}> = ({ value, onChange, swatches, allowAlpha = true }) => {
    const { t } = useTranslation();
    const { hex, alpha } = parseColorWithAlpha(value);
    const [draft, setDraft] = useState(hex);
    useEffect(() => { setDraft(hex); }, [hex]);
    const emit = (h: string, a: number) => onChange(a >= 0.999 ? h : rgba(h, a));
    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={hex}
                    onChange={(e) => emit(e.target.value, alpha)}
                    className="h-8 w-9 shrink-0 cursor-pointer rounded-lg border border-border bg-transparent p-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none [&::-webkit-color-swatch-wrapper]:p-0.5"
                    aria-label={t('DESIGN_EDITOR.COLOR_PICK')}
                />
                <input
                    value={draft}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        if (/^#[0-9a-f]{6}$/i.test(e.target.value)) emit(e.target.value.toLowerCase(), alpha);
                    }}
                    className="h-8 min-w-0 flex-1 rounded-lg border border-border bg-background px-2 text-xs tabular-nums text-foreground outline-none focus:border-foreground/40"
                    aria-label={t('DESIGN_EDITOR.COLOR_HEX')}
                />
                {allowAlpha && (
                    <span className="w-10 shrink-0 text-end text-xs tabular-nums text-muted-foreground">{Math.round(alpha * 100)}%</span>
                )}
            </div>
            {allowAlpha && (
                <Slider min={0} max={1} step={0.01} value={[alpha]} onValueChange={(v) => emit(hex, v[0])} aria-label={t('DESIGN_EDITOR.COLOR_OPACITY')} />
            )}
            <div className="grid grid-cols-9 gap-1.5">
                {swatches.map((c) => (
                    <button
                        key={c}
                        type="button"
                        title={c}
                        aria-label={c}
                        onClick={() => emit(c, alpha)}
                        className={cn(
                            'aspect-square cursor-pointer rounded-md border border-foreground/10 transition-transform hover:scale-110',
                            hex === c.toLowerCase() && 'ring-[1.5px] ring-foreground ring-offset-1 ring-offset-background',
                        )}
                        style={{ backgroundColor: c }}
                    />
                ))}
            </div>
        </div>
    );
};

/**
 * A colour control: swatch trigger + popover with solid (and optionally
 * gradient) editing. Used for every fill, stroke, shadow and artboard colour.
 */
export const ColorField: React.FC<{
    value: Paint | undefined;
    onChange: (p: Paint) => void;
    swatches: string[];
    allowGradient?: boolean;
    title: string;
    /** Render as a full-width row with a label instead of a bare swatch. */
    label?: string;
    glyph?: string;
    compact?: boolean;
}> = ({ value, onChange, swatches, allowGradient, title, label, glyph, compact }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const grad = isGradient(value);
    const [mode, setMode] = useState<'solid' | 'gradient'>(grad ? 'gradient' : 'solid');
    useEffect(() => { if (open) setMode(isGradient(value) ? 'gradient' : 'solid'); }, [open, value]);
    const solid = typeof value === 'string' && value ? value : grad ? gradientParts(value!).a : '#000000';
    const parts = gradientParts(value || solid);

    const trigger = glyph ? (
        <span className="flex flex-col items-center gap-[3px] leading-none">
            <span className="text-[14px] leading-none font-medium text-foreground">{glyph}</span>
            <span className="h-[4px] w-[18px] rounded-full ring-1 ring-foreground/15" style={{ background: paintToCss(value) }} />
        </span>
    ) : (
        <span className="size-5 shrink-0 rounded-md ring-1 ring-foreground/15" style={{ background: paintToCss(value) }} />
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                {label ? (
                    <button
                        type="button"
                        title={title}
                        className="flex h-8 w-full cursor-pointer items-center gap-2 rounded-lg border border-transparent bg-foreground/[0.045] px-2 text-xs text-foreground transition-colors hover:bg-foreground/[0.07]"
                    >
                        {trigger}
                        <span className="min-w-0 flex-1 truncate text-start">{label}</span>
                        <span className="text-muted-foreground tabular-nums">{grad ? t('DESIGN_EDITOR.FILL_GRADIENT') : parseColorWithAlpha(solid).hex.toUpperCase()}</span>
                    </button>
                ) : (
                    <BarButton title={title} className={compact ? 'min-w-7 px-1.5' : undefined}>{trigger}</BarButton>
                )}
            </PopoverTrigger>
            <PopoverContent align="start" sideOffset={6} className="w-[264px] p-3">
                {allowGradient && (
                    <div className="mb-3 grid grid-cols-2 gap-1 rounded-lg bg-foreground/[0.05] p-0.5">
                        {(['solid', 'gradient'] as const).map((m) => (
                            <button
                                key={m}
                                type="button"
                                onClick={() => {
                                    setMode(m);
                                    if (m === 'solid' && grad) onChange(parts.a);
                                    if (m === 'gradient' && !grad) onChange(buildGradient(solid, '#FFFFFF', 'down'));
                                }}
                                className={cn(
                                    'h-7 cursor-pointer rounded-md text-xs transition-colors',
                                    mode === m ? 'bg-background font-medium text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {m === 'solid' ? t('DESIGN_EDITOR.FILL_SOLID') : t('DESIGN_EDITOR.FILL_GRADIENT')}
                            </button>
                        ))}
                    </div>
                )}
                {mode === 'solid' || !allowGradient ? (
                    <SolidPicker value={solid} onChange={(c) => onChange(c)} swatches={swatches} />
                ) : (
                    <div className="flex flex-col gap-3">
                        <div className="h-8 rounded-lg ring-1 ring-foreground/10" style={{ background: paintToCss(value) }} />
                        <div className="grid grid-cols-3 gap-1">
                            {(['down', 'right', 'diagonal'] as const).map((d) => (
                                <ToggleChip key={d} active={parts.dir === d} onClick={() => onChange(buildGradient(parts.a, parts.b, d))}>
                                    {t(`DESIGN_EDITOR.GRAD_${d.toUpperCase()}`)}
                                </ToggleChip>
                            ))}
                        </div>
                        <SectionLabel>{t('DESIGN_EDITOR.GRAD_FROM')}</SectionLabel>
                        <SolidPicker value={parts.a} onChange={(c) => onChange(buildGradient(c, parts.b, parts.dir))} swatches={swatches} />
                        <SectionLabel>{t('DESIGN_EDITOR.GRAD_TO')}</SectionLabel>
                        <SolidPicker value={parts.b} onChange={(c) => onChange(buildGradient(parts.a, c, parts.dir))} swatches={swatches} />
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};

/** A row of icon buttons where one is chosen. */
export function IconSegment<T extends string>({
    value,
    options,
    onChange,
}: {
    value: T | null;
    options: Array<{ value: T; label: string; icon: React.ReactNode }>;
    onChange: (v: T) => void;
}) {
    return (
        <div className="flex gap-0.5 rounded-lg bg-foreground/[0.045] p-0.5">
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    title={o.label}
                    aria-label={o.label}
                    aria-pressed={value === o.value}
                    onClick={() => onChange(o.value)}
                    className={cn(
                        'flex h-7 flex-1 cursor-pointer items-center justify-center rounded-md transition-colors [&_svg]:size-4',
                        value === o.value ? 'bg-background text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
                    )}
                >
                    {o.icon}
                </button>
            ))}
        </div>
    );
}
